import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invitationsApi, type InvitationInfo } from '../api/organizations';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { PASSWORD_MIN } from '../utils/password';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useI18n();

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    invitationsApi
      .getInvitation(token)
      .then((d) => setInfo(d))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await invitationsApi.acceptInvitation(token, info?.userExists
        ? undefined
        : { firstName, lastName, password }
      );
      // Persist new JWT (auto-login)
      localStorage.setItem('dashboard_zce_token', result.token);
      localStorage.setItem('dashboard_zce_user', JSON.stringify(result.user));
      // Force reload to refresh AuthContext from storage
      window.location.href = '/';
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredCard><div className="text-gray-500">{t('team_accept_loading')}</div></CenteredCard>;
  }

  if (error || !info) {
    return (
      <CenteredCard>
        <div className="space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error || t('team_accept_invalid')}
          </div>
          <button onClick={() => navigate('/login')}
            className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            {t('team_backToLogin')}
          </button>
        </div>
      </CenteredCard>
    );
  }

  const roleKey = `team_role_${info.role}`;
  const roleText = t(roleKey as Parameters<typeof t>[0]);
  const roleLabel = roleText === roleKey ? info.role : roleText;

  return (
    <CenteredCard>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('team_accept_title')}</h1>
      <p className="text-sm text-gray-600 mb-6">
        {t('team_accept_invitedTo')} <strong>{info.orgName}</strong> {t('team_accept_as')} <em>{roleLabel}</em>.
      </p>
      <div className="mb-4 p-3 rounded bg-gray-50 text-sm">
        <span className="text-gray-600">{t('team_accept_emailLabel')}</span>
        <strong>{info.email}</strong>
      </div>

      {info.userExists ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('team_accept_accountExists')} {isAuthenticated
              ? t('team_accept_clickToJoin')
              : t('team_accept_loginFirst')}
          </p>
          {isAuthenticated ? (
            <form onSubmit={accept}>
              <button type="submit" disabled={submitting}
                className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {submitting ? t('team_accept_processing') : t('team_accept_join')}
              </button>
            </form>
          ) : (
            <button onClick={() => navigate('/login', { state: { from: { pathname: `/invitations/${token}` } } })}
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
              {t('team_signIn')}
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={accept} className="space-y-3">
          <p className="text-sm text-gray-600">
            {t('team_accept_complete')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('team_firstName')} *</label>
              <input required value={firstName} onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('team_lastName')} *</label>
              <input required value={lastName} onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('team_password')} *</label>
            <input type="password" required minLength={PASSWORD_MIN} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder={t('team_accept_pwdPh', { n: PASSWORD_MIN })} autoComplete="new-password" />
            <PasswordStrengthMeter password={password} />
          </div>
          {error && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{error}</div>}
          <button type="submit" disabled={submitting}
            className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
            {submitting ? t('team_accept_creating') : t('team_accept_createAndJoin')}
          </button>
        </form>
      )}
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8">{children}</div>
      </div>
    </div>
  );
}
